import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import Flag from "react-world-flags";
import { LOCAL_STORAGE_OPEN_PRE_TEST_DIALOG } from "../../../constants/app-constants";
import { LANGUAGES } from "../../../constants/language";
import {
  getCurrencyFromLocalStorage,
  setCurrencyInLocalStorage,
} from "../../../helpers/localStorageHelper";
import { i18n, initializeI18n } from "../../../localization/i18n";
import {
  changeLanguageByUser,
  translate,
} from "../../../localization/translateService";
import { AuthenticationService } from "../../../services/authService";
import { getCurrencyOptions } from "../../../services/currencyService";
import { getLanguageOptions } from "../../../services/languageService";
import { postChangeCurrency } from "../../../services/userService";
import LoadingButton from "../../common/LoadingButton";
import MainNavigationAi from "./MainNavigationAi";
const PreTestModal = React.lazy(() => import("../../PreTestModal"));
const AdvertiseModal = React.lazy(() => import("../../AdvertiseModal"));

export default function HeaderAi({ props }) {
  const {
    portalUrls,
    aiLogo,
    defaultAccountAvatar,
  } = props;
  const languageAndCurrencyDropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const [
    languageAndCurrencyDropdownVisible,
    setLanguageAndCurrencyDropdownVisible,
  ] = useState(false);

  const currentUser$ = AuthenticationService.instance().getCurrentUser$();

  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(!!currentUser);
  const [updateProfileRequired, setUpdateProfileRequired] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [translations, setTranslations] = useState({});
  const [accountOptions, setAccountOptions] = useState([]);
  const [preTestModalOpen, setPreTestModalOpen] = useState(false);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const handleClosePreTestModal = () => {
    setPreTestModalOpen(false);
  };
  const [isSaving, setIsSaving] = useState(false);
  const [accountDropdownVisible, setAccountDropdownVisible] = useState(false);
  const isAdmin = useMemo(() => !!currentUser?.roles?.includes("Admin"), [currentUser?.roles]);
  const isTrainer = useMemo(() => !!currentUser?.roles?.includes("Trainer"), [currentUser?.roles]);
  const isStudent = useMemo(() => !!currentUser?.roles?.includes("Student"), [currentUser?.roles]);

  useEffect(() => {
    const subscription = currentUser$.subscribe((user) => {
      setCurrentUser(user);
      setIsUserLoggedIn(!!user);
      if (user && user.userName === user.email) {
        setUpdateProfileRequired(true);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [currentUser$]);

  const languageCurrentValueRef = useRef(null);
  const currencyCurrentValueRef = useRef(null);

  const parsePathSegments = () =>
    window.location.pathname.split("/").filter(Boolean);


  const buildLanguagePath = (lang) => {
    if (lang === "en") return "/en";
    if (lang === "vi") return "/vi";
    if (lang === "ko") return "/ko";
    return "/en";
  };

    const languagePath = buildLanguagePath(selectedLanguage);

  const getCurrencyShortTitle = (value) => {
    return (
      currencyOptions.find((item) => item.value === value)?.shortName || ""
    );
  };

  const getLanguageTitle = (value) => {
    return languageOptions.find((item) => item.value === value)?.name || "";
  };

  const getLanguageCode = (value) => {
    return languageOptions.find((item) => item.value === value)?.code || "US";
  };

  const handleSaveLanguageAndCurrency = async () => {
    setIsSaving(true);
    if (
      languageCurrentValueRef.current === selectedLanguage &&
      currencyCurrentValueRef.current === selectedCurrency
    ) {
      languageAndCurrencyDropdownRef.current?.hide();
      setIsSaving(false);
      return;
    }
    const isChangeLanguage = await handleLanguageChange(selectedLanguage);
    await handleCurrencyChange(selectedCurrency);
    setIsSaving(false);
    if (!isChangeLanguage) {
      window.location.reload();
    }
  };

  const handleLanguageChange = async (newLang) => {
    if (newLang === languageCurrentValueRef.current) return false;

    const segments = parsePathSegments();
    if (LANGUAGES.includes(segments[0])) {
      segments.shift();
    }

    await changeLanguageByUser(newLang);
    return true;
  };

  const handleCurrencyChange = async (newCurrency) => {
    if (newCurrency === currencyCurrentValueRef.current) return;
    const user = AuthenticationService.instance().getCurrentUser();
    if (user) {
      user.currency = newCurrency;
      AuthenticationService.instance().setCurrentUser(user);
      await postChangeCurrency({ currency: newCurrency });
    } else {
      setCurrencyInLocalStorage(newCurrency, { explicit: true });
    }
  };

  const handleShowLanguageAndCurrencyDropdown = () => {
    setSelectedLanguage(languageCurrentValueRef.current);
    setSelectedCurrency(currencyCurrentValueRef.current);
  };


  const languageOptionTemplate = (option) => {
    return (
      <div className="custom-language-dropdown">
        <Flag code={option?.code} />
        <span>{option.name}</span>
      </div>
    );
  };

  const accountOptionTemplate = (option) => {
    return <span>{option?.name}</span>;
  };

  useEffect(() => {
    if (!i18n.isInitialized) {
      initializeI18n().then(() => {
        languageCurrentValueRef.current = i18n.language;
        setSelectedLanguage(i18n.language);
      });
    } else {
      languageCurrentValueRef.current = i18n.language;
      setSelectedLanguage(i18n.language);
    }
  }, []);

  useEffect(() => {
    const user = AuthenticationService.instance().getCurrentUser();
    if (user) {
      currencyCurrentValueRef.current = user.currency;
      setSelectedCurrency(user.currency);
    } else {
      const storedCurrency = getCurrencyFromLocalStorage();
      currencyCurrentValueRef.current = storedCurrency;
      setSelectedCurrency(storedCurrency);
    }
  }, []);

  useEffect(() => {
    if (!selectedLanguage) return;
    const loadTranslations = async () => {
      const t = {};
      t.homeTitle = await translate("UMBRACO_LANDING.HEADER.MENU.HOME");
      t.coursesTitle = await translate("UMBRACO_LANDING.HEADER.MENU.COURSES");
      t.trainersTitle = await translate("UMBRACO_LANDING.HEADER.MENU.TRAINERS");
      t.aboutUsTitle = await translate("UMBRACO_LANDING.HEADER.MENU.ABOUT_US");
      t.contactUsTitle = await translate(
        "UMBRACO_LANDING.HEADER.MENU.CONTACT_US"
      );
      t.trainerRegistrationTitle = await translate(
        "UMBRACO_LANDING.HEADER.MENU.TRAINER_REGISTRATION"
      );
      t.findYourTrainerTitle = await translate(
        "UMBRACO_LANDING.HEADER.MENU.FIND_YOUR_TRAINER"
      );
      t.realTimeLecturesTitle = await translate(
        "UMBRACO_LANDING.HEADER.MENU.1_1_COURSE"
      );

      t.vietnamese = await translate("LANGUAGES.VI");

      t.english = await translate("LANGUAGES.EN");

      t.korean = await translate("LANGUAGES.KO");

      t.myProfileTitle = await translate("DASHBOARD_MENU_ITEMS.PROFILE");
      t.profileUpdateRequiredTitle = await translate("DASHBOARD_MENU_ITEMS.PROFILE_UPDATE_REQUIRED");
      t.profileUpdateRequiredActionTitle = await translate("DASHBOARD_MENU_ITEMS.PROFILE_UPDATE_REQUIRED_ACTION");
      t.currencyUsdTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.USD",
        false,
        "USD (미국 달러)"
      );

      t.currencyKrwTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.KRW",
        false,
        "KRW (한국원)"
      );

      t.currencyVndTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.VND",
        false,
        "VND (베트남 동)"
      );

      t.currencyUsdShortTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.USD_SHORT",
        false,
        "달러"
      );

      t.currencyKrwShortTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.KRW_SHORT",
        false,
        "원"
      );

      t.currencyVndShortTitle = await translate(
        "CURRENCY.MENU_ITEM_TITLE.VND_SHORT",
        false,
        "동"
      );

      t.adminPortalTitle = await translate(
        "ACCOUNT.MENU_ITEM_TITLE.ADMIN_PORTAL",
        false,
        "Go to Admin Portal"
      );

      t.contentAdminTitle = await translate(
        "ACCOUNT.MENU_ITEM_TITLE.CONTENT_ADMIN",
        false,
        "Go to Content Admin"
      );

      t.trainerPortalTitle = await translate(
        "ACCOUNT.MENU_ITEM_TITLE.TRAINER_PORTAL",
        false,
        "Go to Trainer Portal"
      );

      t.studentPortalTitle = await translate(
        "ACCOUNT.MENU_ITEM_TITLE.STUDENT_PORTAL",
        false,
        "Go to Student Portal"
      );

      t.logOutTitle = await translate(
        "ACCOUNT.MENU_ITEM_TITLE.LOG_OUT",
        false,
        "Log out"
      );

      t.becomeTutorTitle = await translate(
        "UMBRACO_LANDING.HEADER.BECOME_TUTOR",
        false,
        "강사신청"
      );

      t.languageTitle = await translate(
        "UMBRACO_LANDING.HEADER.LANGUAGE",
        false,
        "언어"
      );

      t.currencyTitle = await translate(
        "UMBRACO_LANDING.HEADER.CURRENCY",
        false,
        "통화"
      );

      t.saveButtonLabel = await translate(
        "UMBRACO_LANDING.HEADER.SAVE",
        false,
        "저장"
      );

      t.loginButtonLabel = await translate(
        "UMBRACO_LANDING.HEADER.LOG_IN",
        false,
        "로그인"
      );

      setTranslations(t);
    };
    loadTranslations();
  }, [selectedLanguage]);

  const profileUrl = portalUrls?.studentProfile || "/portals/student/profile";

  const updateLangModalHoldPositionClass = (addAction) => {
    const element = document.querySelector('.custom-menubar-dropdown');
    if(element) {
        if (addAction) {
          element.classList.add('custom-menubar-dropdown-hold-position');
        } else {
          element.classList.remove('custom-menubar-dropdown-hold-position');
        }
    };
  } 

  useEffect(() => {
    const options = [];
    if (isAdmin) {
      options.push({
        name: translations?.adminPortalTitle,
        route: portalUrls?.adminPortal || "/portals/admin",
      });
    }
    if (isTrainer && !props?.tutorPortalDeactivated) {
      options.push({
        name: translations?.trainerPortalTitle,
        route: portalUrls?.trainerPortal || "/portals/trainer",
      });
    }
    if (isStudent && !props?.studentPortalDeactivated) {
      options.push({
        name: translations?.studentPortalTitle,
        route: portalUrls?.studentPortal || "/portals/student",
      });
    }
    setAccountOptions(options);
  }, [
    props,
    translations,
    isAdmin,
    isTrainer,
    isStudent,
    portalUrls,
  ]);

  useEffect(() => {
    const isOpenPreTestModal = localStorage.getItem(LOCAL_STORAGE_OPEN_PRE_TEST_DIALOG);
    if (isOpenPreTestModal === "true") {
      setPreTestModalOpen(true);
      localStorage.removeItem(LOCAL_STORAGE_OPEN_PRE_TEST_DIALOG);
    }    
  }, [])

  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const options = await getLanguageOptions();
        
        const translationPromises = options.map(async (option) => {
          const translatedName = await translate(option.name, true);
          return {
            ...option,
            name: translatedName
          };
        });
        
        const translatedOptions = await Promise.all(translationPromises);
        setLanguageOptions(translatedOptions);
      } catch (error) {
        console.error('Error fetching language options:', error);
      }
    };

    fetchLanguageOptions();
  }, []);

  useEffect(() => {
    const fetchCurrencyOptions = async () => {
      try {
        const options = await getCurrencyOptions();
        
        const translationPromises = options.map(async (option) => {
          const [translatedName, translatedShortName] = await Promise.all([
            translate(option.name, true),
            translate(option.shortName, true)
          ]);
          
          return {
            ...option,
            name: translatedName,
            shortName: translatedShortName
          };
        });
        
        const translatedOptions = await Promise.all(translationPromises);
        setCurrencyOptions(translatedOptions);
      } catch (error) {
        console.error('Error fetching currency options:', error);
      }
    };

    fetchCurrencyOptions();
  }, []);

  const handleOpenPopup = (popup) => {    
    if(popup === 'FREE_TEST' && !props?.preTestDeactivated) {
      setPreTestModalOpen(true);
    }
  }

  // Handle click outside to close account dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setAccountDropdownVisible(false);
      }
    };

    if (accountDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountDropdownVisible]);

  const renderAvatarProfile = () => {
    return (
      <>
        <div className="cursor-pointer text-white h-1rem">
          <i id="notification-box"></i>
        </div>
        <div className="cursor-pointer text-white h-1rem">
          <i id="shopping-cart-box"></i>
        </div>
        <div
          className="header-avatar-dropdown"
          ref={accountDropdownRef}
        >
          <div
            className="avatar-placeholder cursor-pointer"
            onClick={() => setAccountDropdownVisible(true)}
          >
            <img
              src={currentUser?.profilePicture || '/assets/lib/avatar/default-avatar.png'}
              alt=""
            />
          </div>
          {accountDropdownVisible && (
            <div className="account-dropdown-popup">
              <div className="account-dropdown-avatar">
                <img
                  src={currentUser?.profilePicture || '/assets/lib/avatar/default-avatar.png'}
                  alt=""
                />
              </div>
              <div
                className="keduall-typography heading-h6-semibold-600 text-center"
              >
                {currentUser?.displayName}
              </div>
              <div className="mb-3">
                <a href={profileUrl} className="keduall-typography text-14-normal-400  text-primary-txt-color">
                  {translations?.myProfileTitle}
                </a>
              </div>
              { updateProfileRequired && (
                <div className="mb-3 border-primary-color border border-2 border-round-md p-3 text-center">
                  {translations?.profileUpdateRequiredTitle}, <a href={profileUrl + "?updateProfile=true"} className="keduall-typography text-14-normal-400  text-primary-txt-color">
                    {translations?.profileUpdateRequiredActionTitle}
                  </a>
                </div>
              )}

              <div className="account-dropdown-divider" />
              <div className="w-full flex flex-column gap-075">
                {accountOptions.map((option, index) => (
                  <a
                    key={index}
                    href={option.route}
                    className="keduall-typography text-16-normal-400 pb-1 account-dropdown-link"
                  >
                    {option.name}
                  </a>
                ))}
              </div>
              <div className="mt-3 w-full flex flex-column gap-075">
                <a href={props?.logoutUrl} className="keduall-typography text-16-semibold-600 text-danger-txt">{translations?.logOutTitle}</a>
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <div>
      <div className="custom-menubar-placeholder"></div>
      <div className="keduall-header custom-menubar fixed flex gap-0 flex-column md:flex-row-reverse align-items-center">
        <div className="top-header px-4">
          <div className="flex justify-content-end align-items-center gap-075">
            <a
              href={`${languagePath}/tutor-onboarding`}
              className="no-underline"
            >
              <div className="button-link line-height-24 text-sm md:text-base font-semibold white-space-nowrap">
                {translations?.becomeTutorTitle}
              </div>
            </a>
            <OverlayPanel
              className="custom-menubar-dropdown"
              ref={languageAndCurrencyDropdownRef}
              onShow={() => {
                updateLangModalHoldPositionClass(true);
                handleShowLanguageAndCurrencyDropdown();
              }}
              onHide={() => {
                updateLangModalHoldPositionClass(false);
                setLanguageAndCurrencyDropdownVisible(false);
              }}
            >
              <div className="header-overlay-wrapper">
                <div>
                  <p className="dropdown-title">
                    {translations?.languageTitle}
                  </p>
                  <Dropdown
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.value)}
                    options={languageOptions}
                    optionLabel="name"
                    itemTemplate={languageOptionTemplate}
                    panelClassName="custom-menubar-dropdown-language"
                    className="w-full"
                    appendTo="self"
                  />
                </div>
                <div>
                  <p className="dropdown-title">
                    {translations?.currencyTitle}
                  </p>
                  <Dropdown
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.value)}
                    options={currencyOptions}
                    optionLabel="name"
                    panelClassName="custom-menubar-dropdown-currency"
                    className="w-full"
                    appendTo="self"
                  />
                </div>
                <LoadingButton
                  onClick={() => handleSaveLanguageAndCurrency()}
                  loading={isSaving}
                  label={translations?.saveButtonLabel}
                />
              </div>
            </OverlayPanel>
            <div
              className="dropdown-link"
              onClick={(e) => {
                languageAndCurrencyDropdownRef.current?.toggle(e);
                setLanguageAndCurrencyDropdownVisible(
                  !languageAndCurrencyDropdownVisible
                );
              }}
            >
              <Flag code={getLanguageCode(languageCurrentValueRef.current)} />
              <span className="language-currency-dropdown-title keduall-typography white-space-nowrap">
                {getLanguageTitle(languageCurrentValueRef.current)},{" "}
                {getCurrencyShortTitle(currencyCurrentValueRef.current)}
              </span>
              {languageAndCurrencyDropdownVisible ? (
                <i className="pi pi-angle-up"></i>
              ) : (
                <i className="pi pi-angle-down"></i>
              )}
            </div>
            <a
              href={`${languagePath}/contact-us`}
              className="no-underline h-1rem"
            >
              <div className="cursor-pointer">
                <i className="pi pi-question-circle"></i>
              </div>
            </a>
            {!isUserLoggedIn ? (
              <a href={props?.loginUrl} className="no-underline">
                <div className="button-link line-height-24 text-sm md:text-base font-semibold white-space-nowrap">
                  {translations?.loginButtonLabel}
                </div>
              </a>
            ) : (
              renderAvatarProfile()
            )}
          </div>
        </div>
        <MainNavigationAi language={languagePath} onOpenPopup={handleOpenPopup} logo={aiLogo}/>
      </div>
      {(preTestModalOpen) && (
        <Suspense fallback={<></>}>
          <PreTestModal
            isPreTestModalOpen={(preTestModalOpen)}
            closePreTestModal={handleClosePreTestModal}
          />
        </Suspense>
      )}
      <Suspense fallback={<></>}>
        <AdvertiseModal />
      </Suspense>
    </div>
  );
}
